import {
  addLeadCompany,
  addLeadFirstName,
  addLeadLastName,
} from '@user/components/Settings/EntityFormSettings/constants'
import { buildNameRequirementErrorMessage } from '@user/components/Settings/EntityFormSettings/config/nameRequirementMode'
import { validateCustomFields } from '@user/components/CustomFields/Editable/validateCustomFields'
import type { IAutoReviewMissingField } from '@user/store/draftLeads/typings'

import { CONTACT_GROUP_ERROR_KEY, NAME_GROUP_ERROR_KEY } from '../typings'
import type {
  UpdateRequiredFieldsErrors,
  UpdateRequiredFieldsFormModel,
} from '../typings'
import { groupMissingFields } from './groupMissingFields'

const REQUIRED = 'Required'

const blank = (value: string | null | undefined) => (value ?? '').trim() === ''

/**
 * Validates the modal for the fields the server reported missing. First name, last name and company
 * always form an OR-group regardless of the Lead form's name requirement mode: one nameGroup error
 * is raised only when all are blank. Phone and email are satisfied by either (contactGroup).
 */
export function validateUpdateRequiredFields(
  values: UpdateRequiredFieldsFormModel,
  missing: IAutoReviewMissingField[],
): UpdateRequiredFieldsErrors {
  const errors: UpdateRequiredFieldsErrors = {}
  const required = <K extends keyof UpdateRequiredFieldsFormModel>(
    key: K,
    isEmpty: boolean,
  ) => {
    if (isEmpty) (errors as Record<string, unknown>)[key] = REQUIRED
  }

  const customFieldIds: number[] = []

  groupMissingFields(missing).forEach((group) => {
    switch (group.kind) {
      case 'nameOrCompany':
        validateName(values, errors)
        break
      case 'phoneOrEmail':
        if (blank(values.phoneNumber) && blank(values.emailAddress))
          errors[CONTACT_GROUP_ERROR_KEY] =
            'Phone Number or Email Address is required'
        break
      case 'branchId':
        required('branchId', blank(values.branchId))
        break
      case 'assignment':
        group.fields.forEach((f) => {
          if (f.fieldKey === 'salesTeamId')
            required('salesTeamId', !values.salesTeamId)
          if (f.fieldKey === 'assigneeId')
            required('assigneeId', blank(values.assigneeId))
          if (f.fieldKey === 'salesFunnelId')
            required('salesFunnelId', !values.salesFunnelId)
          if (f.fieldKey === 'salesFunnelStageId')
            required('salesFunnelStageId', !values.salesFunnelStageId)
        })
        break
      case 'additional':
        validateAdditional(group.key, values, required)
        break
      case 'custom':
        customFieldIds.push(group.fieldId)
        break
      default:
        break
    }
  })

  if (customFieldIds.length > 0) {
    const customErrors = validateCustomFields(
      values,
      customFieldIds.map((customFieldId) => ({
        customFieldId,
        required: true,
        enabled: true,
        order: 0,
        name: '',
      })),
    )
    if (customErrors.customFields)
      errors.customFields = customErrors.customFields
  }

  return errors
}

function validateName(
  values: UpdateRequiredFieldsFormModel,
  errors: UpdateRequiredFieldsErrors,
) {
  const anyFilled =
    !blank(values.firstName) ||
    !blank(values.lastName) ||
    !blank(values.company)

  if (anyFilled) return

  errors[NAME_GROUP_ERROR_KEY] = buildNameRequirementErrorMessage([
    addLeadFirstName,
    addLeadLastName,
    addLeadCompany,
  ])
}

function validateAdditional(
  key: string,
  values: UpdateRequiredFieldsFormModel,
  required: (
    key: keyof UpdateRequiredFieldsFormModel,
    isEmpty: boolean,
  ) => void,
) {
  switch (key) {
    case 'divisionId':
      required('divisionId', blank(values.divisionId))
      break
    case 'altPhoneNumber':
      required('altPhoneNumber', blank(values.altPhoneNumber))
      break
    case 'address':
      required('street1', blank(values.street1))
      required('city', blank(values.city))
      required('stateAbbreviation', blank(values.stateAbbreviation))
      required('postalCode', blank(values.postalCode))
      break
    case 'locationTypeId':
      required('locationTypeId', blank(values.locationTypeId))
      break
    case 'note':
      required('note', blank(values.note))
      break
    case 'targetPests':
      required('targetPestIds', !(values.targetPestIds?.length > 0))
      break
    case 'campaignId':
      required('campaignId', blank(values.campaignId))
      break
    case 'leadSourceId':
      required('sourceId', blank(values.sourceId))
      break
    case 'referral':
      required(
        'referralCode',
        blank(values.referralCode) && blank(values.referringBillTo),
      )
      break
  }
}
